import { extractTokenKeys } from "../api/translationKeys";
import type { SyncAutoCapturedItem } from "../api/translationKeys";

// Block types whose content carries author-written {{key}}-able text, mapped
// to the content field name(s) that hold it. Types not listed here are
// skipped entirely. Kept in sync with the block types/fields wired for
// translation controls in Item.jsx and EditActivity.jsx — extend this map
// alongside later phases as more block types/fields gain translation
// support. headline1/headline2/paragraph are handled separately below since
// their `content` is a plain string, not an object with named fields.
const TRANSLATABLE_FIELDS: Record<string, string[]> = {
  textbox: ["label"],
  textarea: ["label"],
  checkbox: ["label"], // options[].label handled separately below
  orderedlist: ["label"], // items[].label handled separately below
  slider: ["label"],
  gauge: [
    "label",
    "xAxisLabel",
    "yAxisLabel",
    "xLowLabel",
    "xHighLabel",
    "yLowLabel",
    "yHighLabel",
    "q1Label",
    "q2Label",
    "q3Label",
    "q4Label",
    "q1Prompt",
    "q2Prompt",
    "q3Prompt",
    "q4Prompt",
  ],
  timer: ["dynamicTimeLabel"],
  // proposalText is a raw HTML string (MinimalEditor's Tiptap instance,
  // getHTML()) — same shape/handling as richtext_dynamic's `data` below.
  text_markup: ["label", "proposalText"],
  twodgauge: [
    "label",
    "xAxisLabel",
    "yAxisLabel",
    "lowProgressMessage",
    "highProgressMessage",
    "placeholderMessage",
  ],
  data_event: ["label"],
  timestamp: ["label"],
  elapsed_time: ["label"],
  audio_note: ["label"],
  video_note: ["label"],
  audio_input: ["label"],
  video_input: ["label"],
  image_note: ["label"],
  image_input: ["label"],
  mental_model_studio: ["label"],
  mental_model_path: ["label"],
  // richtext_dynamic's content is { data: "<html>", ... } — data is a raw
  // HTML string (Tiptap getHTML()). extractTokenKeys just regex-matches
  // {{key}} across the string with no HTML awareness, which is safe here
  // since extraction only needs to find which keys are referenced, not
  // safely substitute within structure (a false-positive match inside an
  // attribute is harmless for extraction, unlike for resolution).
  richtext_dynamic: ["data"],
};

// Phase 7: block types whose translatable text lives nested one level
// deeper, inside content.config (edited via a shared sub-component like
// SmartListConfig/MentalModelStudioConfig, not a bare input directly in
// EditActivity.jsx). Checked in addition to TRANSLATABLE_FIELDS above, not
// instead of it — e.g. mental_model_studio/path have both a flat `.label`
// (via TRANSLATABLE_FIELDS) and a nested `config.intro` (via this map).
const TRANSLATABLE_CONFIG_FIELDS: Record<string, string[]> = {
  smart_list: ["title", "placeholder"],
  categories_board: ["title", "placeholder"],
  // Shares SmartListConfig as its editing UI (same config.title/placeholder
  // shape) — see EditActivity.jsx's "thoughts_organizer"/"smart_list"/
  // "categories_board" shared case.
  thoughts_organizer: ["title", "placeholder"],
  mental_model_studio: ["intro"],
  mental_model_path: ["intro"],
};

// Author-typed strings on block.settings (sibling of content), not inside
// content/config. aiprompt deferred-execution UI copy lives here.
const TRANSLATABLE_SETTINGS_FIELDS: Record<string, string[]> = {
  aiprompt: ["triggerButtonText", "preButtonMessage"],
};

// richtextnew's content is also a plain string (raw HTML from Tiptap's
// getHTML()), same shape as headline1/headline2/paragraph — extractTokenKeys
// is HTML-agnostic but that's fine for extraction (see richtext_dynamic
// comment above for why). aiprompt/interpolation store the prompt/template
// body as a plain string (with optional {fieldKey} placeholders) on
// block.content — captured via block.__tid like the other string types.
const STRING_CONTENT_TYPES = new Set([
  "headline1",
  "headline2",
  "paragraph",
  "richtextnew",
  "aiprompt",
  "interpolation",
]);

/**
 * Walks a prescription diagram's `data` map (`{ [nodeId]: { custom: [...] } }`,
 * the same shape DesignView.tsx/EditActivity.jsx already pass around as
 * `allActivitiesData`) and returns the union of {{key}} names referenced in
 * any translatable block's text across all nodes — see TRANSLATABLE_FIELDS
 * and STRING_CONTENT_TYPES above for exactly which block types/fields.
 */
export function extractPrescriptionActivityKeys(
  data: Record<string, any> | null | undefined
): string[] {
  const names = new Set<string>();
  if (!data || typeof data !== "object") return [];

  for (const node of Object.values(data)) {
    if (!node || typeof node !== "object") continue;
    const custom = (node as any).custom;
    if (!Array.isArray(custom)) continue;

    for (const block of custom) {
      if (!block || typeof block !== "object") continue;

      if (STRING_CONTENT_TYPES.has(block.type)) {
        // headline1/headline2/paragraph/aiprompt/interpolation: content is a
        // plain string, not an object — was silently skipped before this fix
        // since the object-only check below (`typeof content !== "object"`)
        // excluded it.
        for (const name of extractTokenKeys(block.content)) names.add(name);
        const settingsFields = TRANSLATABLE_SETTINGS_FIELDS[block.type];
        if (settingsFields && block.settings && typeof block.settings === "object") {
          for (const field of settingsFields) {
            for (const name of extractTokenKeys(block.settings[field])) names.add(name);
          }
        }
        continue;
      }

      const fields = TRANSLATABLE_FIELDS[block.type];
      const configFields = TRANSLATABLE_CONFIG_FIELDS[block.type];
      const settingsFields = TRANSLATABLE_SETTINGS_FIELDS[block.type];
      if (!fields && !configFields && !settingsFields) continue;
      const content = block.content;
      if ((!content || typeof content !== "object") && !settingsFields) continue;

      if (fields && content && typeof content === "object") {
        for (const field of fields) {
          for (const name of extractTokenKeys(content[field])) names.add(name);
        }

        if (Array.isArray(content.options)) {
          for (const option of content.options) {
            for (const name of extractTokenKeys(option?.label)) names.add(name);
          }
        }
        if (Array.isArray(content.items)) {
          for (const item of content.items) {
            for (const name of extractTokenKeys(item?.label)) names.add(name);
          }
        }
      }

      if (configFields && content?.config && typeof content.config === "object") {
        for (const field of configFields) {
          for (const name of extractTokenKeys(content.config[field])) names.add(name);
        }
      }

      if (settingsFields && block.settings && typeof block.settings === "object") {
        for (const field of settingsFields) {
          for (const name of extractTokenKeys(block.settings[field])) names.add(name);
        }
      }
    }
  }

  return Array.from(names);
}

// --- Auto-capture translation (replaces the {{key}} notation flow above) ---
//
// Every translatable field (same TRANSLATABLE_FIELDS/TRANSLATABLE_CONFIG_FIELDS/
// STRING_CONTENT_TYPES registry as extractPrescriptionActivityKeys above —
// that registry still defines which fields are text, it's unchanged) gets a
// hidden sibling property holding its link to a translation-table row,
// instead of requiring {{key}} notation inside the text itself. Naming
// convention: a flat field `label` gets a sibling `label__tid`; an array
// item (checkbox option, orderedlist item) gets the id as a sibling property
// directly on that array element, e.g. `option.label__tid`.

function makeFieldId(): string {
  const uuid =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  return `tk_${uuid}`;
}

interface FieldLink {
  tid: string;
  text: string;
}

// Plain strings are empty when trimmed-blank; HTML content (richtextnew's
// own content, richtext_dynamic's content.data) can be "visually empty" but
// non-blank as a string, e.g. Tiptap's empty-document output "<p></p>" — so
// strip tags before checking those two specifically. A plain tag-strip is
// sufficient here (unlike resolution, this only needs an emptiness check,
// not a safe text-node walk).
const HTML_CONTENT_FIELDS = new Set([
  "richtextnew:content",
  "richtext_dynamic:data",
  "text_markup:proposalText",
  // contenteditable residue (&nbsp;, <br>) on aiprompt/interpolation bodies
  "aiprompt:content",
  "interpolation:content",
]);

function isEmptyText(text: string, htmlKey?: string): boolean {
  if (htmlKey && HTML_CONTENT_FIELDS.has(htmlKey)) {
    // Strip tags and common contenteditable residue (&nbsp;, bare entities)
    // so empty Interpolation/TipTap shells don't create sync rows.
    return (
      text
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&[a-z]+;/gi, " ")
        .trim().length === 0
    );
  }
  return text.trim().length === 0;
}

// Reads a field's current hidden id + text, assigning a fresh id in place
// (mutating `container`) only when the field actually has non-empty text —
// an empty/untouched field (freshly added block, never typed into) gets no
// id and no sync entry, so it never creates a blank placeholder row. A
// field that already has an id but is now empty (author cleared it) is also
// skipped here deliberately: its existing row isn't synced/updated, simply
// stops being referenced by any live field — which is exactly how the
// Translations page's "unused" section detects it (present in the saved
// key pool, absent from this walk's current result), per the "never
// auto-delete, just stop syncing" orphan-handling decision. `container` is
// the object that directly owns `field` (a block's `content`,
// `content.config`, or an array element like a checkbox option) — never the
// text value itself.
function linkField(container: Record<string, any>, field: string, htmlKey?: string): FieldLink | null {
  const text = container[field];
  if (typeof text !== "string" || isEmptyText(text, htmlKey)) return null;

  const idField = `${field}__tid`;
  let tid = container[idField];
  if (typeof tid !== "string" || !tid) {
    tid = makeFieldId();
    container[idField] = tid;
  }
  return { tid, text };
}

// For blocks whose whole `content` is a plain string (headline1/headline2/
// paragraph/richtextnew) there's no sibling object to hang `label__tid` off
// of — the id is tracked instead via `block.__tid` on the block itself. Same
// empty-text handling as linkField above.
function linkStringContent(block: Record<string, any>, htmlKey?: string): FieldLink | null {
  const text = block.content;
  if (typeof text !== "string" || isEmptyText(text, htmlKey)) return null;

  let tid = block.__tid;
  if (typeof tid !== "string" || !tid) {
    tid = makeFieldId();
    block.__tid = tid;
  }
  return { tid, text };
}

// Strips every auto-capture translation link (`__tid`, or `<field>__tid`
// siblings) from a deep-cloned value — used when a block's content is
// duplicated (node duplicate, or copy+paste) so the duplicate doesn't keep
// pointing at the original's translation-table rows. Without this, editing
// one copy's wording would silently mark the OTHER copy's row as stale,
// since both would resolve to the same row — a duplicated label is new
// content, not a reference to the original. NOT used for move/cut
// operations (the content isn't becoming a second instance, so its existing
// links should be preserved) or for PrescriptionHistory version snapshots
// (archival copies of the same content, not new content). Generic/recursive
// rather than naming every field again, so it stays correct automatically
// as the translatable-field registry above grows.
export function stripTranslationLinks(value: any): any {
  if (Array.isArray(value)) {
    return value.map(stripTranslationLinks);
  }
  if (value && typeof value === "object") {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      if (k === "__tid" || k.endsWith("__tid")) continue;
      out[k] = stripTranslationLinks(v);
    }
    return out;
  }
  return value;
}

/**
 * Walks a prescription diagram's `data` map, same shape as
 * extractPrescriptionActivityKeys above, and:
 *  1. Mutates `data` in place to assign a hidden `__tid` to every
 *     translatable field that doesn't already have one (new content since
 *     the last save).
 *  2. Returns a flat list of every translatable field's current
 *     { tid, baseLang, text } — the payload for the sync endpoint
 *     (api/translationKeys.ts's syncAutoCapturedKeys), one entry per field
 *     instance (so a checkbox with 3 options contributes 1 label entry + 3
 *     option entries).
 *
 * Called from DesignView.tsx's generateDiagram(), on the same `cleanedData`
 * object that's about to be saved — so any ids newly assigned here are the
 * exact ones persisted to diagramJSON, not generated separately and risking
 * drift between what's saved and what's synced.
 */
export function collectTranslatableFieldLinks(
  data: Record<string, any> | null | undefined,
  baseLang: "en" | "de" | "he"
): SyncAutoCapturedItem[] {
  const items: SyncAutoCapturedItem[] = [];
  if (!data || typeof data !== "object") return items;

  const pushLink = (link: FieldLink | null) => {
    if (link) items.push({ tid: link.tid, baseLang, text: link.text });
  };

  for (const node of Object.values(data)) {
    if (!node || typeof node !== "object") continue;
    const custom = (node as any).custom;
    if (!Array.isArray(custom)) continue;

    for (const block of custom) {
      if (!block || typeof block !== "object") continue;

      if (STRING_CONTENT_TYPES.has(block.type)) {
        const htmlKey =
          block.type === "richtextnew"
            ? "richtextnew:content"
            : block.type === "aiprompt" || block.type === "interpolation"
              ? `${block.type}:content`
              : undefined;
        pushLink(linkStringContent(block, htmlKey));
        const settingsFields = TRANSLATABLE_SETTINGS_FIELDS[block.type];
        if (settingsFields && block.settings && typeof block.settings === "object") {
          for (const field of settingsFields) {
            pushLink(linkField(block.settings, field));
          }
        }
        continue;
      }

      // personal_diagram: author text on nodes + optional labels on edges
      if (block.type === "personal_diagram" && block.content && typeof block.content === "object") {
        if (Array.isArray(block.content.nodes)) {
          for (const pdNode of block.content.nodes) {
            if (pdNode && typeof pdNode === "object") {
              pushLink(linkField(pdNode, "authorText"));
            }
          }
        }
        if (Array.isArray(block.content.edges)) {
          for (const pdEdge of block.content.edges) {
            if (pdEdge && typeof pdEdge === "object") {
              pushLink(linkField(pdEdge, "label"));
            }
          }
        }
        continue;
      }

      const fields = TRANSLATABLE_FIELDS[block.type];
      const configFields = TRANSLATABLE_CONFIG_FIELDS[block.type];
      const settingsFields = TRANSLATABLE_SETTINGS_FIELDS[block.type];
      if (!fields && !configFields && !settingsFields) continue;
      const content = block.content;
      if ((!content || typeof content !== "object") && !settingsFields) continue;

      if (fields && content && typeof content === "object") {
        for (const field of fields) {
          const candidateKey = `${block.type}:${field}`;
          const htmlKey = HTML_CONTENT_FIELDS.has(candidateKey) ? candidateKey : undefined;
          pushLink(linkField(content, field, htmlKey));
        }

        if (Array.isArray(content.options)) {
          for (const option of content.options) {
            if (option && typeof option === "object") {
              pushLink(linkField(option, "label"));
            }
          }
        }
        if (Array.isArray(content.items)) {
          for (const item of content.items) {
            if (item && typeof item === "object") {
              pushLink(linkField(item, "label"));
            }
          }
        }
      }

      if (configFields && content?.config && typeof content.config === "object") {
        for (const field of configFields) {
          pushLink(linkField(content.config, field));
        }
      }

      if (settingsFields && block.settings && typeof block.settings === "object") {
        for (const field of settingsFields) {
          pushLink(linkField(block.settings, field));
        }
      }
    }
  }

  return items;
}

/**
 * Read-only counterpart to collectTranslatableFieldLinks, for display
 * surfaces (the Translations page) that must never mint new `__tid`s or
 * mutate the fetched diagram — only report the links a field already has.
 * A field with no `__tid` yet (not captured by an autosave cycle) simply
 * contributes no row; it will appear once the author's next edit is saved.
 */
export function collectExistingTranslationLinks(
  data: Record<string, any> | null | undefined
): { tid: string; text: string }[] {
  const items: { tid: string; text: string }[] = [];
  if (!data || typeof data !== "object") return items;

  const pushExisting = (container: Record<string, any>, field: string) => {
    const text = container[field];
    const tid = container[`${field}__tid`];
    if (typeof tid === "string" && tid && typeof text === "string") {
      items.push({ tid, text });
    }
  };

  const pushExistingString = (block: Record<string, any>) => {
    const text = block.content;
    const tid = block.__tid;
    if (typeof tid === "string" && tid && typeof text === "string") {
      items.push({ tid, text });
    }
  };

  for (const node of Object.values(data)) {
    if (!node || typeof node !== "object") continue;
    const custom = (node as any).custom;
    if (!Array.isArray(custom)) continue;

    for (const block of custom) {
      if (!block || typeof block !== "object") continue;

      if (STRING_CONTENT_TYPES.has(block.type)) {
        pushExistingString(block);
        const settingsFields = TRANSLATABLE_SETTINGS_FIELDS[block.type];
        if (settingsFields && block.settings && typeof block.settings === "object") {
          for (const field of settingsFields) {
            pushExisting(block.settings, field);
          }
        }
        continue;
      }

      if (block.type === "personal_diagram" && block.content && typeof block.content === "object") {
        if (Array.isArray(block.content.nodes)) {
          for (const pdNode of block.content.nodes) {
            if (pdNode && typeof pdNode === "object") pushExisting(pdNode, "authorText");
          }
        }
        if (Array.isArray(block.content.edges)) {
          for (const pdEdge of block.content.edges) {
            if (pdEdge && typeof pdEdge === "object") pushExisting(pdEdge, "label");
          }
        }
        continue;
      }

      const fields = TRANSLATABLE_FIELDS[block.type];
      const configFields = TRANSLATABLE_CONFIG_FIELDS[block.type];
      const settingsFields = TRANSLATABLE_SETTINGS_FIELDS[block.type];
      if (!fields && !configFields && !settingsFields) continue;
      const content = block.content;
      if ((!content || typeof content !== "object") && !settingsFields) continue;

      if (fields && content && typeof content === "object") {
        for (const field of fields) {
          pushExisting(content, field);
        }

        if (Array.isArray(content.options)) {
          for (const option of content.options) {
            if (option && typeof option === "object") pushExisting(option, "label");
          }
        }
        if (Array.isArray(content.items)) {
          for (const item of content.items) {
            if (item && typeof item === "object") pushExisting(item, "label");
          }
        }
      }

      if (configFields && content?.config && typeof content.config === "object") {
        for (const field of configFields) {
          pushExisting(content.config, field);
        }
      }

      if (settingsFields && block.settings && typeof block.settings === "object") {
        for (const field of settingsFields) {
          pushExisting(block.settings, field);
        }
      }
    }
  }

  return items;
}
